// DAS-2107 / DAS-2151: "Your Timeline" modal for PortfolioAnalysisView
// Displays upcoming and past events: earnings + dividends (ex-dates).
// Dividend data comes from Dividends_Historical via pa_dividend_data.

import React, { useState, useMemo } from 'react';
import { Box, Typography, Paper, Chip, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SavingsIcon from '@mui/icons-material/Savings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClickableTickerChip from './ClickableTickerChip';

const EVENT_COLORS = {
  Earnings: '#0097a7',
  Dividend: '#2e7d32',
  'Dividend (est.)': '#66bb6a',
  Interest: '#7b1fa2',
};

const PAModal_Timeline = ({ yfData, divData = {}, depositInterestData = [], data, source = 'portfolio' }) => {
  const [filter, setFilter] = useState('all');

  const events = useMemo(() => {
    const items = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Earnings events from yfData
    Object.entries(yfData).forEach(([ticker, yf]) => {
      if (!yf.next_earnings_date) return;
      const dateStr = String(yf.next_earnings_date);
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return;

      const diffMs = date - today;
      const daysUntil = Math.round(diffMs / (1000 * 60 * 60 * 24));
      const position = data.find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());

      items.push({
        ticker,
        date,
        dateStr: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
        daysUntil,
        company: position?.Company || '',
        sector: yf.sector || '',
        type: 'Earnings',
        isPast: daysUntil < 0,
        detail: null,
      });
    });

    // Dividend events from divData
    Object.entries(divData).forEach(([ticker, dd]) => {
      const position = data.find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());
      const company = position?.Company || '';
      const posSize = parseFloat(position?.Position) || 0;

      const buildDivDetail = (amount, prefix = '') => {
        if (!amount || amount <= 0) return null;
        const perShare = `${prefix}$${amount.toFixed(4)}/sh`;
        if (posSize > 0) {
          const total = amount * posSize;
          return `${perShare} · ${prefix}$${total.toFixed(2)} total`;
        }
        return perShare;
      };

      if (dd.past) {
        dd.past.forEach(div => {
          const date = new Date(div.ex_date);
          if (isNaN(date.getTime())) return;
          const diffMs = date - today;
          const daysUntil = Math.round(diffMs / (1000 * 60 * 60 * 24));

          const payDate = div.pay_date ? new Date(div.pay_date) : null;
          const payDateStr = payDate && !isNaN(payDate.getTime())
            ? payDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : null;

          items.push({
            ticker,
            date,
            dateStr: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
            daysUntil,
            company,
            sector: '',
            type: 'Dividend',
            isPast: daysUntil < 0,
            detail: buildDivDetail(div.amount),
            isSpecial: div.type === 'SC',
            payDateStr,
          });
        });
      }

      if (dd.next_estimated) {
        const est = dd.next_estimated;
        const date = new Date(est.ex_date);
        if (!isNaN(date.getTime())) {
          const diffMs = date - today;
          const daysUntil = Math.round(diffMs / (1000 * 60 * 60 * 24));

          const payDate = est.pay_date ? new Date(est.pay_date) : null;
          const payDateStr = payDate && !isNaN(payDate.getTime())
            ? payDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : null;

          items.push({
            ticker,
            date,
            dateStr: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
            daysUntil,
            company,
            sector: '',
            type: 'Dividend (est.)',
            isPast: false,
            detail: buildDivDetail(est.amount, '~'),
            payDateStr,
          });
        }
      }
    });

    // DAS-2168: Deposit interest payout events
    if (depositInterestData && depositInterestData.length > 0) {
      depositInterestData.forEach(ev => {
        const date = new Date(ev.date);
        if (isNaN(date.getTime())) return;
        const diffMs = date - today;
        const daysUntil = Math.round(diffMs / (1000 * 60 * 60 * 24));

        const payoutLabel = ev.payout_type === 'maturity' ? 'Maturity' : 'Quarterly';
        const amountStr = ev.amount > 0 ? `€${ev.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
        const rateStr = ev.rate ? `${ev.rate}%` : '';
        const detail = [amountStr, rateStr ? `at ${rateStr}` : '', payoutLabel].filter(Boolean).join(' · ');

        items.push({
          ticker: ev.label || ev.institution || 'Deposit',
          date,
          dateStr: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
          daysUntil,
          company: ev.institution || '',
          sector: ev.description || '',
          type: 'Interest',
          isPast: daysUntil < 0,
          detail,
          isDeposit: true,
        });
      });
    }

    // Deduplicate: if a past dividend is also an upcoming one (same ticker+date), keep only one
    const seen = new Set();
    return items
      .sort((a, b) => a.date - b.date)
      .filter(e => {
        const typeKey = e.type.startsWith('Dividend') ? 'div' : e.type === 'Interest' ? 'int' : 'earn';
        const key = `${e.ticker}-${e.date.toISOString().slice(0, 10)}-${typeKey}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [yfData, divData, depositInterestData, data]);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    if (filter === 'earnings') return events.filter(e => e.type === 'Earnings');
    if (filter === 'dividends') return events.filter(e => e.type.startsWith('Dividend'));
    if (filter === 'interest') return events.filter(e => e.type === 'Interest');
    return events;
  }, [events, filter]);

  const upcoming = filtered.filter(e => !e.isPast);
  const past = filtered.filter(e => e.isPast);

  const earningsCount = events.filter(e => e.type === 'Earnings').length;
  const dividendCount = events.filter(e => e.type.startsWith('Dividend')).length;
  const interestCount = events.filter(e => e.type === 'Interest').length;

  const renderEventRow = (event, idx) => {
    const typeColor = EVENT_COLORS[event.type] || '#0097a7';

    const urgencyColor = event.isPast
      ? 'text.disabled'
      : event.daysUntil <= 7
        ? '#c62828'
        : event.daysUntil <= 30
          ? '#e65100'
          : typeColor;

    const daysLabel = event.isPast
      ? `${Math.abs(event.daysUntil)}d ago`
      : event.daysUntil === 0
        ? 'Today'
        : `in ${event.daysUntil}d`;

    const chipColor = event.type === 'Earnings' ? 'info' : event.type === 'Interest' ? 'secondary' : 'success';

    return (
      <Box
        key={`${event.ticker}-${event.type}-${idx}`}
        display="flex"
        alignItems="flex-start"
        gap={2}
        sx={{
          py: 1.5,
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          opacity: event.isPast ? 0.5 : 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ width: 4, minHeight: 40, bgcolor: urgencyColor, borderRadius: 1, flexShrink: 0, mt: 0.5 }} />

        <Box sx={{ minWidth: 90, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: urgencyColor }}>
            {daysLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {event.dateStr}
          </Typography>
        </Box>

        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {event.isDeposit ? (
              <Chip label={event.ticker} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
            ) : (
              <ClickableTickerChip ticker={event.ticker} source={source} />
            )}
            <Chip
              icon={event.type === 'Earnings' ? <EventIcon sx={{ fontSize: 14 }} /> : event.type === 'Interest' ? <SavingsIcon sx={{ fontSize: 14 }} /> : <MonetizationOnIcon sx={{ fontSize: 14 }} />}
              label={event.isSpecial ? 'Special Div' : event.type}
              size="small"
              color={chipColor}
              variant={event.type === 'Dividend (est.)' ? 'outlined' : 'filled'}
              sx={{ fontSize: '0.65rem', height: 22 }}
            />
            {event.detail && (
              <Typography variant="caption" sx={{ fontWeight: 600, color: typeColor }}>
                {event.detail}
              </Typography>
            )}
            {event.type.startsWith('Dividend') && (
              <Tooltip
                arrow
                placement="top"
                title="Ex-date: last day to buy and still receive the dividend. Payment date: when the money arrives in your account."
              >
                <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
          {event.payDateStr && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              Payment: {event.payDateStr}{event.type === 'Dividend (est.)' ? ' (est.)' : ''}
            </Typography>
          )}
          {event.company && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {event.company}
            </Typography>
          )}
          {event.sector && (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ fontStyle: 'italic' }}>
              {event.sector}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (events.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No events available for portfolio tickers</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
          size="small"
        >
          <ToggleButton value="all">
            All ({events.length})
          </ToggleButton>
          <ToggleButton value="earnings">
            <EventIcon sx={{ fontSize: 16, mr: 0.5 }} /> Earnings ({earningsCount})
          </ToggleButton>
          <ToggleButton value="dividends">
            <MonetizationOnIcon sx={{ fontSize: 16, mr: 0.5 }} /> Dividends ({dividendCount})
          </ToggleButton>
          {interestCount > 0 && (
            <ToggleButton value="interest">
              <SavingsIcon sx={{ fontSize: 16, mr: 0.5 }} /> Interest ({interestCount})
            </ToggleButton>
          )}
        </ToggleButtonGroup>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" color="text.secondary">
            {upcoming.length} upcoming · {past.length} past
          </Typography>
          {dividendCount > 0 && (
            <Tooltip arrow placement="top" title="Dividend dates shown are ex-dividend dates (the cut-off for ownership). Payment dates (when money arrives) are shown separately where available.">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
      </Box>

      {upcoming.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Upcoming
          </Typography>
          <Paper variant="outlined">
            {upcoming.map(renderEventRow)}
          </Paper>
        </Box>
      )}

      {past.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Past
          </Typography>
          <Paper variant="outlined">
            {past.map(renderEventRow)}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default PAModal_Timeline;
